import { OperationVariables } from "@apollo/client";
import type { Unmasked } from "@apollo/client/masking";
import { QueryResult } from "@apollo/client/react/types/types";
import { concatAndDedupeById } from "@mx/gpl-common";
import { DocumentNode } from "graphql";
import React from "react";
import { Maybe } from "../graphql/gpl-graphQLTypes.gen";
import { useMemoizedCallback } from "./hooks/useMemoizedCallback";

type MakeOptional<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;
export type FetchMoreFn = (() => Promise<any> | any) | undefined;
export type FetchMoreWithLimitFn = ((limit?: number) => Promise<any> | any) | undefined;

type FetchMoreType<Data, Args extends OperationVariables> = QueryResult<Data, Args>["fetchMore"];

type IPaginationResult = {
  hasMore: boolean;
  cursor: string;
  data?: any[];
};

export enum MX_PAGINATION_LIMIT {
  NONE = 0,
  MINUSCULE = 3,
  TINY = 5,
  VERY_SMALL = 10,
  SMALL = 25,
  NORMAL = 40,
  BIG = 60,
  HUGE = 100,
}

type B<T> = Exclude<T, null | undefined>;
export function makeMoreFactory<
  Data,
  Args extends OperationVariables & {
    pagination?: { cursor?: Maybe<string>; limit?: Maybe<number>; cursorType?: Maybe<string> };
  },
>(
  document: DocumentNode,
  limit: MX_PAGINATION_LIMIT,
  opts: {
    onVariables?: (variables: Args) => void;
    insertOrder?: "BEGINNING" | "END";
    handleInsert?: <T>(prev: T[], more: T[]) => T[];
  } = {},
) {
  return function withPathToPagination<
    K1 extends keyof Data,
    K2 extends keyof B<Data[K1]> = never,
    K3 extends keyof B<B<Data[K1]>[K2]> = never,
    K4 extends keyof B<B<B<Data[K1]>[K2]>[K3]> = never,
    K5 extends keyof B<B<B<B<Data[K1]>[K2]>[K3]>[K4]> = never,
  >(
    ...pathToPagination: B<Data[K1]> extends IPaginationResult
      ? [K1]
      : B<B<Data[K1]>[K2]> extends IPaginationResult
      ? [K1, K2]
      : B<B<B<Data[K1]>[K2]>[K3]> extends IPaginationResult
      ? [K1, K2, K3]
      : B<B<B<B<Data[K1]>[K2]>[K3]>[K4]> extends IPaginationResult
      ? [K1, K2, K3, K4]
      : B<B<B<B<B<Data[K1]>[K2]>[K3]>[K4]>[K5]> extends IPaginationResult
      ? [K1, K2, K3, K4, K5]
      : never
  ) {
    const readFromPath = (obj: Data | Unmasked<Data> | undefined) => {
      let value: any = obj;
      for (const p of pathToPagination) {
        value = value?.[p];
      }
      return value as IPaginationResult | undefined;
    };
    type ArgsPaginationOptional = MakeOptional<Args, "pagination">;

    function fetchMore(
      _fetchMore: FetchMoreType<Data, Args>,
      variablesSansPagination: ArgsPaginationOptional,
      cursor: string,
      overrideLimit?: number,
    ) {
      const variables = {
        ...variablesSansPagination,
        pagination: {
          cursor,
          limit: overrideLimit ?? limit,
          cursorType: variablesSansPagination.pagination?.cursorType,
        },
      };
      return _fetchMore({
        query: document,
        variables,
        updateQuery(previousQueryResult, { fetchMoreResult }) {
          opts.onVariables?.(variables as any);
          const previous = readFromPath(previousQueryResult);
          if (!previous) {
            return previousQueryResult;
          }
          const paginated = readFromPath(fetchMoreResult);
          const newData = paginated?.data || [];
          const newCursor = paginated?.cursor;
          const hasMore = paginated?.hasMore;
          const previousData = previous?.data || [];
          const result: any = { ...previousQueryResult };
          let current = result;
          for (const p of pathToPagination) {
            current[p] = { ...current[p] };
            current = current[p];
          }
          const dataToConcat =
            opts.insertOrder === "END"
              ? { beginning: newData, end: previousData }
              : { beginning: previousData, end: newData };
          // eslint-disable-next-line no-nested-ternary
          const data = opts?.handleInsert
            ? opts.handleInsert(previousData, newData)
            : previousData[0] && typeof previousData[0] === "object" && "id" in previousData[0]
            ? concatAndDedupeById(dataToConcat.beginning, dataToConcat.end)
            : [...dataToConcat.beginning, ...dataToConcat.end];

          // current now points to paginated result
          Object.assign(current, {
            hasMore,
            cursor: newCursor,
            data,
          });
          return result;
        },
      });
    }

    useWithFetchMore.getCallback = function useGetCallback<Result extends QueryResult<any, any>>(
      queryResult: Result,
      variablesSansPagination: ArgsPaginationOptional,
    ): FetchMoreWithLimitFn {
      const { cursor, hasMore } = readFromPath(queryResult.data) || {};
      const onFetchMore = useMemoizedCallback((limit?: unknown) =>
        fetchMore(
          queryResult.fetchMore,
          variablesSansPagination,
          cursor || "",
          // Just in case fetchMore is passed directly to an event handler
          // don't want to accidentally send the event as the limit
          typeof limit === "number" ? limit : undefined,
        ),
      );
      return hasMore ? onFetchMore : undefined;
    };
    function useWithFetchMore<
      Result extends QueryResult<any, any>,
      Dest extends string = "onFetchMore",
    >(
      queryResult: Result,
      variablesSansPagination: ArgsPaginationOptional,
      destination: Dest = "onFetchMore" as Dest,
    ) {
      const onFetchMore = useWithFetchMore.getCallback(queryResult, variablesSansPagination);
      const newResult = React.useMemo(
        () => ({
          ...queryResult,
          [destination]: onFetchMore,
        }),
        [queryResult, destination, onFetchMore],
      );
      return newResult as Result & { [k in Dest]: FetchMoreWithLimitFn };
    }
    return useWithFetchMore;
  };
}
